import { useLocalStorageState } from 'ahooks';
import { useCallback, useEffect, useRef } from 'react';
import {
  FEATURE_TOURS_STORAGE_KEY,
  ONE_DAY,
  ONE_WEEK,
  PROCESSING_TOURS_STORAGE_KEY,
  TOUR_FUNCTION_TIPS_LIST,
  TOUR_KEYS,
  TOUR_METADATA_CONFIG,
  type TourKeyType,
} from '@/app/constants';
import { useStore } from '@/app/providers/store';
import type { ToursStorage } from '@/app/types';
import { periodOfValidity } from '@/app/utils';
import { useIsScreen } from './use-is-screen';

/**
 * Tour 队列协调器
 *
 * 协调规则：
 * 1. 用户登录后，根据配置将符合条件的 Tour 加入队列
 * 2. 管理会话级别的 Tour 显示计数
 * 3. 激活逻辑由各个 FeatureTour 组件自己处理
 *
 */
export const useTourCoordinator = () => {
  const userInfo = useStore((state) => state.userInfo);
  const { isSmScreen } = useIsScreen();

  const { addToTourQueue } = useStore((state) => ({
    addToTourQueue: state.addToTourQueue,
  }));

  // 已完成的 Tour 列表
  const [completedTours] = useLocalStorageState<TourKeyType[]>(FEATURE_TOURS_STORAGE_KEY, {
    defaultValue: [],
    listenStorageChange: true,
  });

  // 进行中的 Tour 数据
  const [processingTours] = useLocalStorageState<ToursStorage>(PROCESSING_TOURS_STORAGE_KEY, {
    defaultValue: {},
    listenStorageChange: true,
  });

  // 使用 ref 标记是否已初始化
  const isInitializedRef = useRef(false);

  // 检查用户注册时间是否满足条件
  const checkUserRegistrationValid = useCallback(
    (tourOnlineTime: string) => {
      if (!userInfo?.created_at) return true;
      if (!tourOnlineTime) return true;

      const onlineTimestamp = new Date(tourOnlineTime).getTime();
      const userCreatedTimestamp = new Date(userInfo.created_at).getTime();
      return onlineTimestamp > userCreatedTimestamp + ONE_WEEK;
    },
    [userInfo],
  );

  // 检查时间有效性
  const checkTimeValid = useCallback(
    (tourKey: TourKeyType, showEndDate?: string, intervalDays?: number) => {
      if (showEndDate && !periodOfValidity(['', showEndDate])) {
        return false;
      }

      const tourData = processingTours?.[tourKey];
      const lastTime = tourData?.lastTime || 0;

      if (intervalDays !== undefined && lastTime > 0) {
        const timeDiff = Date.now() - lastTime;
        const daysDiff = timeDiff / ONE_DAY;
        return daysDiff >= intervalDays;
      }

      return true;
    },
    [processingTours],
  );

  // 初始化队列（只执行一次）
  useEffect(() => {
    // 未登录跳过
    if (!userInfo) return;
    // 如果已经初始化过，跳过
    if (isInitializedRef.current) return;
    // 遍历所有 Tour 配置，检查是否应该加入队列
    Object.values(TOUR_KEYS).forEach((tourKey) => {
      const metadata = TOUR_METADATA_CONFIG[tourKey];
      if (!metadata) return;

      // 检查是否在小屏幕上禁用
      if (metadata.disableOnSmallScreen && isSmScreen) {
        return;
      }

      // 检查是否已完成
      if (completedTours?.includes(tourKey)) {
        return;
      }

      // 检查是否在功能提示列表中
      if (!TOUR_FUNCTION_TIPS_LIST.includes(tourKey)) {
        // 检查时间有效性
        if (!checkTimeValid(tourKey, metadata.showEndDate, metadata.intervalDays)) {
          return;
        }

        // 检查用户注册时间
        if (metadata.tourOnlineTime && !checkUserRegistrationValid(metadata.tourOnlineTime)) {
          return;
        }
      }

      // 所有条件都满足，将 Tour 加入队列
      addToTourQueue({
        id: tourKey,
        priority: metadata.priority,
      });
    });

    // 标记已初始化
    isInitializedRef.current = true;
  }, [userInfo, isSmScreen, completedTours]);
};
